import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, Package, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getImageUrl } from '../utils/imageUtils';

const normalizeVariantValue = (value) => {
  if (value == null) return null;
  const cleaned = String(value).trim();
  return cleaned || null;
};

const productHasDistinctVariants = (product) => {
  if (!product) return false;

  const variants = Array.isArray(product.variant_stock) ? product.variant_stock : [];
  if (variants.length > 0) {
    return variants.some(
      (v) => normalizeVariantValue(v.color) || normalizeVariantValue(v.size)
    );
  }

  if (product.product_type === 'gear' && Array.isArray(product.sizes) && product.sizes.length > 0) {
    return true;
  }

  if (Array.isArray(product.colors) && product.colors.length > 0) {
    return true;
  }

  return false;
};

const getInStockVariants = (product) => {
  const variants = Array.isArray(product?.variant_stock) ? product.variant_stock : [];
  return variants.filter((v) => Number(v?.stock || 0) > 0);
};

const getAvailableColors = (product) => {
  const variants = getInStockVariants(product);
  const colors = new Set();
  variants.forEach((v) => {
    if (normalizeVariantValue(v.color)) {
      colors.add(normalizeVariantValue(v.color));
    }
  });
  return Array.from(colors);
};

const getAvailableSizesForColor = (product, selectedColor) => {
  const variants = getInStockVariants(product);
  const sizes = new Set();
  variants.forEach((v) => {
    if (normalizeVariantValue(v.color) === selectedColor && normalizeVariantValue(v.size)) {
      sizes.add(normalizeVariantValue(v.size));
    }
  });
  return Array.from(sizes);
};

const buildDefaultSelection = (item) => {
  const product = item?.product;
  if (!product) {
    return { product_id: item.product_id };
  }

  // Prefer locked combo-item color/size if present
  if (item.color || item.size) {
    return {
      product_id: item.product_id,
      color: normalizeVariantValue(item.color),
      size: normalizeVariantValue(item.size),
    };
  }

  // Default / simple variant_stock entry (no color/size)
  const variants = Array.isArray(product.variant_stock) ? product.variant_stock : [];
  if (variants.length > 0 && !productHasDistinctVariants(product)) {
    const defaultVariant = variants.find(
      (v) => !normalizeVariantValue(v.color) && !normalizeVariantValue(v.size) && Number(v.stock || 0) > 0
    ) || variants.find((v) => Number(v.stock || 0) > 0);

    return {
      product_id: item.product_id,
      color: normalizeVariantValue(defaultVariant?.color),
      size: normalizeVariantValue(defaultVariant?.size),
    };
  }

  return { product_id: item.product_id };
};

const ComboVariantSelector = ({ open, onClose, combo, onConfirm, loading = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState({});
  const [isConfirming, setIsConfirming] = useState(false);
  const confirmedRef = useRef(false);
  const selectionsRef = useRef({});
  const [selectedColorForSizes, setSelectedColorForSizes] = useState(null);

  // Only keep combo children that still exist as active products
  const allItems = useMemo(
    () => (combo?.combo_items || []).filter((item) => item?.product_id && item?.product),
    [combo]
  );

  // Steps only for products that actually need a variant choice
  const selectableItems = useMemo(
    () => allItems.filter((item) => productHasDistinctVariants(item.product)),
    [allItems]
  );

  const currentItem = selectableItems[currentStep] || null;
  const currentProduct = currentItem?.product || null;

  useEffect(() => {
    selectionsRef.current = selections;
  }, [selections]);

  const finalizeSelections = (latestSelections = selectionsRef.current) => {
    return allItems.map((item) => {
      const picked = latestSelections[item.product_id];
      if (picked && (picked.variantSelected || picked.color || picked.size)) {
        return {
          product_id: item.product_id,
          color: normalizeVariantValue(picked.color),
          size: normalizeVariantValue(picked.size),
        };
      }
      return buildDefaultSelection(item);
    });
  };

  const confirmSelections = (latestSelections) => {
    if (confirmedRef.current || isConfirming) return;
    confirmedRef.current = true;
    setIsConfirming(true);
    onConfirm(finalizeSelections(latestSelections));
  };

  useEffect(() => {
    if (!open) {
      confirmedRef.current = false;
      setSelectedColorForSizes(null);
      return;
    }

    setCurrentStep(0);
    setSelections({});
    setIsConfirming(false);
    confirmedRef.current = false;
    setSelectedColorForSizes(null);

    // No products with selectable variants — add combo directly
    if (allItems.length > 0 && selectableItems.length === 0 && !loading) {
      const timer = setTimeout(() => {
        confirmSelections({});
      }, 50);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, allItems.length, selectableItems.length]);

  // Allow retry if parent reports add-to-cart failure (loading ends, modal still open)
  const wasLoadingRef = useRef(false);
  useEffect(() => {
    if (wasLoadingRef.current && !loading && open && isConfirming) {
      confirmedRef.current = false;
      setIsConfirming(false);
    }
    wasLoadingRef.current = loading;
  }, [loading, open, isConfirming]);

  const handleVariantSelect = (variant) => {
    if (!currentItem?.product_id) return;
    setSelections((prev) => ({
      ...prev,
      [currentItem.product_id]: {
        color: normalizeVariantValue(variant.color),
        size: normalizeVariantValue(variant.size),
        variantSelected: true,
      },
    }));
  };

  const handleSizeSelect = (size) => {
    if (!currentItem?.product_id) return;
    setSelections((prev) => ({
      ...prev,
      [currentItem.product_id]: {
        ...prev[currentItem.product_id],
        size: normalizeVariantValue(size),
        variantSelected: true,
      },
    }));
  };

  const handleColorSelect = (color) => {
    if (!currentItem?.product_id) return;
    setSelectedColorForSizes(normalizeVariantValue(color));
    setSelections((prev) => ({
      ...prev,
      [currentItem.product_id]: {
        ...prev[currentItem.product_id],
        color: normalizeVariantValue(color),
        size: null, // Reset size when color changes
        variantSelected: true,
      },
    }));
  };

  const handleNext = () => {
    if (isConfirming || confirmedRef.current) return;

    if (currentStep < selectableItems.length - 1) {
      setCurrentStep((prev) => prev + 1);
      return;
    }

    confirmSelections(selectionsRef.current);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const isLastStep = selectableItems.length === 0 || currentStep === selectableItems.length - 1;
  const currentSelection = currentItem?.product_id ? selections[currentItem.product_id] || {} : {};
  const inStockVariants = currentProduct ? getInStockVariants(currentProduct) : [];
  const availableColors = currentProduct ? getAvailableColors(currentProduct) : [];
  const availableSizesForColor = selectedColorForSizes ? getAvailableSizesForColor(currentProduct, selectedColorForSizes) : [];

  const hasVariantStockUI = inStockVariants.length > 0;
  const hasColorSizeUI = hasVariantStockUI && availableColors.length > 0;
  const hasLegacyUI =
    !hasVariantStockUI &&
    ((currentProduct?.product_type === 'gear' && currentProduct?.sizes?.length > 0) ||
      (currentProduct?.colors?.length > 0));

  const canProceed = () => {
    if (!currentProduct || !currentItem?.product_id) return false;

    const selection = selections[currentItem.product_id] || {};

    // Handle new color-size UI
    if (hasColorSizeUI) {
      if (!selection.color) return false;
      // Only require size if sizes exist for the selected color
      if (selectedColorForSizes && availableSizesForColor.length > 0 && !selection.size) {
        return false;
      }
      return true;
    }

    if (Array.isArray(currentProduct.variant_stock) && currentProduct.variant_stock.length > 0) {
      if (!selection.variantSelected) return false;

      const stock = getInStockVariants(currentProduct).find(
        (v) =>
          normalizeVariantValue(v.color) === normalizeVariantValue(selection.color) &&
          normalizeVariantValue(v.size) === normalizeVariantValue(selection.size)
      );
      return Boolean(stock);
    }

    if (currentProduct.product_type === 'gear' && !selection.size) {
      return false;
    }

    if (currentProduct.colors?.length > 0 && !selection.color) {
      return false;
    }

    return true;
  };

  if (!open) {
    return null;
  }

  // Combo has no valid child products
  if (allItems.length === 0) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-2xl text-white p-6 shadow-2xl"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-zinc-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <Package className="w-10 h-10 text-zinc-500 mb-3" />
            <h2 className="text-lg font-bold mb-2">Combo unavailable</h2>
            <p className="text-sm text-gray-400 mb-4">
              This combo has no available products. Please try another combo.
            </p>
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-zinc-800 rounded-lg hover:bg-zinc-700"
            >
              Close
            </button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Auto-confirming (no selectable variants) — brief loading state
  if (selectableItems.length === 0) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        >
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl text-white p-6 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-[#ffcc32] border-t-transparent rounded-full animate-spin" />
            <span>Adding combo to cart...</span>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  if (!currentItem || !currentProduct) {
    return null;
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-zinc-900 border border-zinc-800 rounded-2xl text-white shadow-2xl"
          >
            <div className="sticky top-0 z-10 bg-zinc-900/95 backdrop-blur border-b border-zinc-800 p-6">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-bold text-white">
                Select Variants for {combo?.name}
              </h2>
              <div className="text-sm text-gray-400 mt-2">
                Step {currentStep + 1} of {selectableItems.length}: {currentProduct.name}
              </div>
            </div>

            <div className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-20 h-20 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0">
                  {(currentProduct.images?.[0] || combo?.thumbnail) ? (
                    <img
                      src={getImageUrl(currentProduct.images?.[0] || combo?.thumbnail)}
                      alt={currentProduct.name}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <Package className="w-8 h-8 text-zinc-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{currentProduct.name}</h3>
                  <p className="text-sm text-gray-400">Quantity: {currentItem.quantity}</p>
                </div>
              </div>

              {hasColorSizeUI ? (
                <>
                  {/* Color Selection */}
                  <div className="mb-6">
                    <h3 className="text-base font-semibold mb-3 block text-white">
                      Select Color *
                    </h3>
                    <div className="grid grid-cols-4 gap-2">
                      {availableColors.map((color) => {
                        const isSelected = currentSelection.color === color;
                        return (
                          <button
                            key={color}
                            type="button"
                            onClick={() => handleColorSelect(color)}
                            className={`px-4 py-2 border-2 rounded-lg font-semibold transition-all text-sm capitalize
                              ${isSelected
                                ? 'bg-[#ffcc32] text-black border-[#ffcc32]'
                                : 'border-zinc-700 text-white hover:border-[#ffcc32] hover:bg-zinc-800'
                              }
                            `}
                          >
                            {color}
                            {isSelected && (
                              <Check className="ml-2 w-3 h-3 text-black inline" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Size Selection - Only show after color is selected */}
                  {selectedColorForSizes && (
                    <div className="mb-6">
                      <h3 className="text-base font-semibold mb-3 block text-white">
                        Select Size *
                      </h3>
                      <div className="grid grid-cols-4 gap-2">
                        {Array.from(new Set(inStockVariants
                          .filter(v => normalizeVariantValue(v.color) === selectedColorForSizes)
                          .map(v => normalizeVariantValue(v.size))
                          .filter(Boolean)
                        )).map((size) => {
                          const isSelected = currentSelection.size === size;
                          const isAvailable = availableSizesForColor.includes(size);
                          
                          return (
                            <button
                              key={size}
                              type="button"
                              disabled={!isAvailable}
                              onClick={() => handleSizeSelect(size)}
                              className={`px-4 py-2 border-2 rounded-lg font-semibold transition-all text-sm
                                ${isSelected
                                  ? 'bg-[#ffcc32] text-black border-[#ffcc32]'
                                  : 'border-zinc-700 text-white hover:border-[#ffcc32] hover:bg-zinc-800'
                                }
                                ${!isAvailable ? 'opacity-40 cursor-not-allowed' : ''}
                              `}
                            >
                              {size}
                              {isSelected && (
                                <Check className="ml-2 w-3 h-3 text-black inline" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                      {availableSizesForColor.length === 0 && (
                        <p className="text-sm text-red-400 mt-2">No sizes available for this color.</p>
                      )}
                    </div>
                  )}
                </>
              ) : hasVariantStockUI ? (
                <div className="mb-6">
                  <h3 className="text-base font-semibold mb-3 block text-white">
                    Select Variant *
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {inStockVariants.map((variant, index) => {
                      const color = normalizeVariantValue(variant.color);
                      const size = normalizeVariantValue(variant.size);
                      const isSelected =
                        currentSelection.variantSelected &&
                        normalizeVariantValue(currentSelection.color) === color &&
                        normalizeVariantValue(currentSelection.size) === size;

                      return (
                        <button
                          key={`${color || 'c'}-${size || 's'}-${index}`}
                          type="button"
                          onClick={() => handleVariantSelect(variant)}
                          className={`relative px-4 py-3 border-2 rounded-lg font-semibold transition-all text-sm
                            ${isSelected
                              ? 'bg-[#ffcc32] text-black border-[#ffcc32]'
                              : 'border-zinc-700 text-white hover:border-[#ffcc32] hover:bg-zinc-800'
                            }
                          `}
                        >
                          <div className="flex flex-col items-center">
                            <span className="font-medium">
                              {[color, size].filter(Boolean).join(' ') || 'Default'}
                            </span>
                            <span className="text-xs opacity-75">{(variant.stock || 0) > 0 ? 'In Stock' : 'Out of Stock'}</span>
                          </div>
                          {isSelected && (
                            <Check className="absolute top-1 right-1 w-3 h-3 text-black" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {inStockVariants.length === 0 && (
                    <p className="text-sm text-red-400 mt-2">No variants in stock for this product.</p>
                  )}
                </div>
              ) : hasLegacyUI ? (
                <>
                  {currentProduct.product_type === 'gear' && (
                    <div className="mb-6">
                      <h3 className="text-base font-semibold mb-3 block text-white">
                        Select Size *
                      </h3>
                      <div className="grid grid-cols-4 gap-2">
                        {(currentProduct.sizes || []).map((size) => {
                          const stock = currentProduct.size_stock?.[size] || 0;
                          const isSelected = currentSelection.size === size;
                          const isOutOfStock = stock <= 0;

                          return (
                            <button
                              key={size}
                              type="button"
                              disabled={isOutOfStock}
                              onClick={() => handleSizeSelect(size)}
                              className={`relative px-4 py-2 border-2 rounded-lg font-semibold transition-all text-sm
                                ${isSelected
                                  ? 'bg-[#ffcc32] text-black border-[#ffcc32]'
                                  : 'border-zinc-700 text-white hover:border-[#ffcc32] hover:bg-zinc-800'
                                }
                                ${isOutOfStock ? 'opacity-50 cursor-not-allowed' : ''}
                              `}
                            >
                              {size}
                              {isOutOfStock && (
                                <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                              )}
                              {isSelected && (
                                <Check className="absolute top-1 right-1 w-3 h-3 text-black" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {currentProduct.colors?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-base font-semibold mb-3 block text-white">
                        Select Color *
                      </h3>
                      <div className="grid grid-cols-4 gap-2">
                        {currentProduct.colors.map((color) => {
                          const isSelected = currentSelection.color === color;
                          return (
                            <button
                              key={color}
                              type="button"
                              onClick={() => handleColorSelect(color)}
                              className={`px-4 py-2 border-2 rounded-lg font-semibold transition-all text-sm capitalize
                                ${isSelected
                                  ? 'bg-[#ffcc32] text-black border-[#ffcc32]'
                                  : 'border-zinc-700 text-white hover:border-[#ffcc32] hover:bg-zinc-800'
                                }
                              `}
                            >
                              {color}
                              {isSelected && (
                                <Check className="ml-2 w-3 h-3 text-black inline" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-gray-400">No variants available for this product.</p>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-zinc-900/95 backdrop-blur border-t border-zinc-800 p-4">
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={currentStep === 0 || loading || isConfirming}
                  className="px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!canProceed() || loading || isConfirming}
                  className="px-4 py-2 bg-[#ffcc32] text-black rounded-lg hover:bg-[#ffda5c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center gap-2"
                >
                  {loading || isConfirming ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : isLastStep ? (
                    <>
                      <Check className="w-4 h-4" />
                      Add to Cart
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ComboVariantSelector;
